export const WALLBOARD_REFRESH_MS = 5000;
export function normalizeWallboardSnapshot(raw) {
    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    return {
        timestamp: raw?.timestamp,
        asteriskConnected: Boolean(raw?.asteriskConnected),
        connectionError: raw?.connectionError ?? null,
        queueTotal: num(raw?.queueTotal),
        serviceLevelPercent: num(raw?.serviceLevelPercent),
        efficiencyLevelPercent: num(raw?.efficiencyLevelPercent),
        online: num(raw?.online),
        averageWaitTimeSeconds: num(raw?.averageWaitTimeSeconds),
        longestWaitTimeSeconds: num(raw?.longestWaitTimeSeconds),
        estimatedWaitTimeSeconds: num(raw?.estimatedWaitTimeSeconds),
        talking: num(raw?.talking),
        lock: num(raw?.lock),
        busy: num(raw?.busy),
        idle: num(raw?.idle),
        leave: num(raw?.leave),
        waitingCallers: num(raw?.waitingCallers),
    };
}
export const WALLBOARD_STAT_CONFIG = [
    {
        label: 'Queue Total',
        field: 'queueTotal',
        format: 'int',
        scaleMin: 0,
        scaleMax: 120,
        healthRule: { kind: 'lower-better', goodMax: 42 },
    },
    {
        label: 'Service Level',
        field: 'serviceLevelPercent',
        format: 'percent',
        scaleMin: 0,
        scaleMax: 100,
        healthRule: { kind: 'higher-better', goodMin: 88 },
    },
    {
        label: 'Efficiency Level',
        field: 'efficiencyLevelPercent',
        format: 'percent',
        scaleMin: 0,
        scaleMax: 100,
        healthRule: { kind: 'higher-better', goodMin: 78 },
    },
    {
        label: 'Online',
        field: 'online',
        format: 'int',
        scaleMin: 0,
        scaleMax: 48,
        healthRule: { kind: 'band', goodMin: 18, goodMax: 34 },
    },
    {
        label: 'AWT',
        field: 'averageWaitTimeSeconds',
        format: 'time',
        scaleMin: 0,
        scaleMax: 185,
        healthRule: { kind: 'lower-better', goodMax: 55 },
    },
    {
        label: 'LWT',
        field: 'longestWaitTimeSeconds',
        format: 'time',
        scaleMin: 0,
        scaleMax: 92,
        healthRule: { kind: 'lower-better', goodMax: 35 },
    },
    {
        label: 'EWT',
        field: 'estimatedWaitTimeSeconds',
        format: 'time',
        scaleMin: 0,
        scaleMax: 240,
        healthRule: { kind: 'lower-better', goodMax: 110 },
    },
    {
        label: 'Talking',
        field: 'talking',
        format: 'int',
        scaleMin: 0,
        scaleMax: 36,
        healthRule: { kind: 'band', goodMin: 8, goodMax: 26 },
    },
    {
        label: 'Lock',
        field: 'lock',
        format: 'int',
        scaleMin: 0,
        scaleMax: 8,
        healthRule: { kind: 'lower-better', goodMax: 2 },
    },
    {
        label: 'Busy',
        field: 'busy',
        format: 'int',
        scaleMin: 0,
        scaleMax: 28,
        healthRule: { kind: 'band', goodMin: 8, goodMax: 22 },
    },
    {
        label: 'Idle',
        field: 'idle',
        format: 'int',
        scaleMin: 0,
        scaleMax: 22,
        healthRule: { kind: 'band', goodMin: 4, goodMax: 14 },
    },
    {
        label: 'Leave',
        field: 'leave',
        format: 'int',
        scaleMin: 0,
        scaleMax: 6,
        healthRule: { kind: 'lower-better', goodMax: 2 },
    },
];
function formatStatValue(format, n) {
    if (format === 'percent') {
        const rounded = Math.round(n * 10) / 10;
        if (Number.isInteger(rounded))
            return `${rounded}%`;
        return `${rounded.toFixed(1)}%`;
    }
    const v = Math.round(n);
    if (format === 'time')
        return `${v}s`;
    return String(v);
}
function computeHealth(c, raw) {
    const { scaleMin: dMin, scaleMax: dMax, healthRule: r } = c;
    if (r.kind === 'band') {
        if (raw >= r.goodMin && raw <= r.goodMax)
            return 1;
        if (raw < r.goodMin) {
            const denom = r.goodMin - dMin;
            return denom > 0 ? Math.max(0, Math.min(1, (raw - dMin) / denom)) : 0;
        }
        const denom = dMax - r.goodMax;
        return denom > 0 ? Math.max(0, Math.min(1, (dMax - raw) / denom)) : 0;
    }
    if (r.kind === 'higher-better') {
        if (raw >= r.goodMin)
            return 1;
        const denom = r.goodMin - dMin;
        return denom > 0 ? Math.max(0, Math.min(1, (raw - dMin) / denom)) : 0;
    }
    if (raw <= r.goodMax)
        return 1;
    const denom = dMax - r.goodMax;
    return denom > 0 ? Math.max(0, Math.min(1, (dMax - raw) / denom)) : 0;
}
const HEALTH_GREEN = { r: 34, g: 197, b: 94 };
const HEALTH_RED = { r: 239, g: 68, b: 68 };
const HEALTH_DARK_RED = { r: 91, g: 18, b: 18 };
const NEUTRAL_COLOR = '#64748b';
function rgbCss(rgb) {
    return `rgb(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)})`;
}
function colorForHealth(health) {
    const h = Math.max(0, Math.min(1, health));
    if (h >= 1)
        return rgbCss(HEALTH_GREEN);
    const severity = 1 - h;
    return rgbCss({
        r: HEALTH_RED.r + (HEALTH_DARK_RED.r - HEALTH_RED.r) * severity,
        g: HEALTH_RED.g + (HEALTH_DARK_RED.g - HEALTH_RED.g) * severity,
        b: HEALTH_RED.b + (HEALTH_DARK_RED.b - HEALTH_RED.b) * severity,
    });
}
export function buildWallboardStatSnapshots(snapshot) {
    return WALLBOARD_STAT_CONFIG.map((c) => {
        if (!snapshot) {
            return { label: c.label, display: '—', numberColor: NEUTRAL_COLOR };
        }
        const raw = snapshot[c.field];
        return {
            label: c.label,
            display: formatStatValue(c.format, raw),
            numberColor: colorForHealth(computeHealth(c, raw)),
        };
    });
}
